<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'

import { ArrowDown, QuestionFilled, User, UserFilled } from '@/shared/icons'
import { useViewport } from '@/shared/lib'
import { Dropdown, DropdownItem, DropdownMenu } from '@/shared/ui/Dropdown'
import { Icon } from '@/shared/ui/Icon'
import { Inline } from '@/shared/ui/Inline'
import { GetBackButton, useAccount } from '@/apps/adok/entities/account'
import { useBalance } from '@/apps/adok/entities/balance'
import { EntitySearch } from '@/apps/adok/widgets/search-entities'

import commonIcons from '../assets'

const ACCOUNT_COMMANDS = ['profile', 'logout'] as const
type AccountCommand = (typeof ACCOUNT_COMMANDS)[number]

const router = useRouter()
const { isMobile } = useViewport()
const { isStaff, accountId, accountName, balanceCents, logOut } = useAccount()

const name = computed(() =>
  isStaff.value ? `${accountId.value}: ${accountName.value}` : accountName.value
)

const id = computed(() => (isStaff.value ? accountId.value : 'Profile'))

const { balance } = useBalance()
const userBalance = computed(() =>
  (balance.value ?? balanceCents.value / 100).toFixed(2)
)

async function onCommand(command: AccountCommand) {
  if (command === 'profile') {
    router.push('/profile')
    return
  }
  await logOut()
  router.push({ name: 'login' })
}
</script>

<template>
  <Inline align="center" :gap="8" class="right-menu">
    <EntitySearch v-if="!isMobile" class="right-menu__search" />
    <router-link to="/balance/summary" class="right-menu__pill">
      <span class="right-menu__currency">$</span>
      {{ userBalance }}
    </router-link>
    <a
      v-if="!isMobile"
      target="_blank"
      rel="noopener"
      href="https://support.adok.ai/"
      class="right-menu__pill right-menu__pill--muted"
    >
      <Icon><QuestionFilled /></Icon>
      Help
    </a>
    <Dropdown :commands="ACCOUNT_COMMANDS" @command="onCommand">
      <span class="right-menu__pill">
        <Icon><UserFilled /></Icon>
        {{ isMobile ? id : name }}
        <Icon><ArrowDown /></Icon>
      </span>
      <template #dropdown>
        <DropdownMenu>
          <DropdownItem v-if="isMobile" disabled>{{ name }}</DropdownItem>
          <DropdownItem v-if="isStaff">
            <GetBackButton />
          </DropdownItem>
          <DropdownItem command="profile" :icon="User">My profile</DropdownItem>
          <DropdownItem command="logout">
            <img :src="commonIcons.logout" class="right-menu__logout-icon" />
            Log Out
          </DropdownItem>
        </DropdownMenu>
      </template>
    </Dropdown>
  </Inline>
</template>

<style lang="scss" scoped>
.right-menu {
  padding: 0 16px;
}

.right-menu__search {
  width: 240px;
}

.right-menu__pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: var(--el-text-color-primary);
  cursor: pointer;
  outline: none;
  transition: background-color var(--el-transition-duration);

  &:hover {
    background: var(--el-fill-color-light);
  }
}

.right-menu__pill--muted {
  color: var(--el-text-color-secondary);
}

.right-menu__currency {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  font-size: 12px;
  font-weight: 700;
  color: var(--el-color-white);
  background: var(--el-color-primary);
}

.right-menu__logout-icon {
  width: 15px;
  margin-right: 5px;
}
</style>
